<template>
  <section class="page-container partner-statistics">
    <el-breadcrumb separator="/" class="report-breadcrumb">
      <el-breadcrumb-item>数据报表</el-breadcrumb-item>
      <el-breadcrumb-item>合伙人数据统计</el-breadcrumb-item>
    </el-breadcrumb>
    <div class="report-heading">
      <div><h2>{{ t('title') }}</h2><span class="muted">{{ t('amountHint') }}</span></div>
    </div>
    <el-card shadow="never" class="filters">
      <el-tabs v-model="mode" :before-leave="beforeModeChange" @input="changeMode">
        <el-tab-pane label="时间段汇总" name="PERIOD" />
        <el-tab-pane label="每日明细" name="DAILY" />
      </el-tabs>
      <div class="mode-row">
        <el-tag type="info" size="small">{{ t('onlyOrdinary') }}</el-tag>
      </div>
      <el-form :inline="true" size="small" @submit.native.prevent="search">
        <el-form-item label="UID"><el-input v-model.trim="uid" class="uid-input" :placeholder="t('exactUid')" clearable maxlength="19" /></el-form-item>
        <el-form-item :label="t('partnerUid')"><el-input v-model.trim="partnerUid" class="uid-input" :placeholder="t('exactUid')" clearable maxlength="19" /></el-form-item>
        <el-form-item>
          <el-select v-model="userType" class="user-type">
            <el-option :label="t('all')" value="ALL" /><el-option :label="t('ordinary')" value="ORDINARY" />
            <el-option :label="t('simulated')" value="SIMULATED" disabled />
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-date-picker v-model="dates" type="daterange" value-format="yyyy-MM-dd"
            :range-separator="t('to')" :start-placeholder="t('start')" :end-placeholder="t('end')"
            :picker-options="pickerOptions" :clearable="false" />
        </el-form-item>
        <el-form-item><el-button type="primary" native-type="submit" :loading="loading">{{ t('search') }}</el-button>
          <el-button @click="reset">{{ t('reset') }}</el-button>
          <el-button v-if="canExport" :loading="exporting" :disabled="loading" @click="exportReport">导出</el-button>
        </el-form-item>
      </el-form>
    </el-card>
    <div class="table-actions">
      <span>{{ t('summary') }} <span class="muted">{{ result.startDate }} — {{ result.endDate }}</span></span>
      <el-button size="small" icon="el-icon-setting" @click="settingsOpen=true">{{ t('columns') }}</el-button>
    </div>
    <el-table :key="'summary:'+summaryColumns.map(c=>c.key).join(',')" :data="result.summaries" border size="small" class="summary-table" :empty-text="t('empty')" row-key="key">
      <el-table-column fixed prop="kind" :label="t('summaryType')" width="150">
        <template slot-scope="s"><strong>{{ t(s.row.kind) }}</strong></template>
      </el-table-column>
      <el-table-column v-for="c in summaryColumns" :key="c.key"
        :label="c.key==='date'?'统计区间':c.label" :width="c.width" :align="c.key==='date'?'left':'right'">
        <template slot-scope="s"><span :class="amountClass(s.row,c.key)">{{ display(s.row,c.key) }}</span></template>
      </el-table-column>
    </el-table>
    <div class="detail-title"><strong>{{ mode==='PERIOD'?'时间段合伙人汇总列表':'每日明细列表' }}</strong><span class="muted">{{ result.total }}</span></div>
    <el-alert v-if="error" :title="error" type="error" :closable="false" show-icon />
    <el-table :key="'detail:'+mode+':'+visibleColumns.map(c=>c.key).join(',')" :data="result.records" v-loading="loading" border stripe size="small" :empty-text="t('empty')"
      max-height="560" row-key="key">
      <el-table-column v-for="c in visibleColumns" :key="c.key" :label="c.label" :width="c.width"
        :fixed="c.key==='uid'?'left':false" :align="['uid','date'].includes(c.key)?'left':'right'">
        <template slot-scope="s">
          <el-tooltip v-if="c.key==='contractVolume' && s.row.notes.includes('CONTRACT_VOLUME_CONFLICT')"
            :content="t('CONTRACT_VOLUME_CONFLICT')" placement="top">
            <span class="uncertain">{{ display(s.row,c.key) }} <i class="el-icon-warning-outline" /></span>
          </el-tooltip>
          <span v-else :class="amountClass(s.row,c.key)">{{ display(s.row,c.key) }}</span>
        </template>
      </el-table-column>
    </el-table>
    <el-pagination class="pagination" background :current-page="page" :page-size="size" :page-sizes="[20,50,100,200]" :disabled="loading"
      :total="result.total" layout="total, sizes, prev, pager, next, jumper"
      @current-change="changePage" @size-change="changeSize" />
    <el-dialog :title="t('columns')" :visible.sync="settingsOpen" width="94%" top="5vh" custom-class="partner-column-dialog" append-to-body>
      <div class="column-actions"><el-button size="mini" @click="selectAll">{{ t('allColumns') }}</el-button>
        <el-button size="mini" @click="restoreColumns">{{ t('restore') }}</el-button></div>
      <p class="muted">{{ t('drag') }}</p>
      <div class="column-list">
        <div class="column-setting fixed-column">
          <i class="el-icon-lock" />
          <el-checkbox :value="true" disabled>{{ label('uid') }}（固定首列）</el-checkbox>
        </div>
        <draggable v-model="movableColumns" class="column-grid" handle=".drag-handle" @end="saveColumns">
          <div v-for="key in movableColumns" :key="key" class="column-setting">
            <i class="el-icon-rank drag-handle" />
            <el-checkbox v-model="selectedColumns" :label="key" @change="saveColumns">{{ label(key) }}</el-checkbox>
          </div>
        </draggable>
      </div>
    </el-dialog>
  </section>
</template>

<script>
import http from 'plugins/axios'
import draggable from 'vuedraggable'
import Decimal from 'decimal.js'
import { hasPerm } from 'common/permission'
import { fields, columnLabels, translate } from './messages'

const DEFAULT_COLUMNS = ['uid','date','contractRate','newRegistrations','registrations','newPartners','partners',
  'chainIn','chainOut','netDeposit','contractVolume','contractFee','netPnl','estimatedRebate','paidRebate','afterRebatePnl']
const EMPTY = () => ({ records: [], summaries: [], total: 0, notes: [], startDate: '', endDate: '' })
function beijingYesterday () {
  const now = new Date(Date.now() + 8*3600000 - 86400000)
  return now.toISOString().slice(0,10)
}
function range(mode) {
  const end = beijingYesterday()
  const start = mode==='PERIOD' ? new Date(Date.parse(end+'T00:00:00Z')-6*86400000).toISOString().slice(0,10) : end
  return [start,end]
}
export default {
  name: 'PartnerDataStatistics',
  components: { draggable },
  data () {
    return { mode:'PERIOD', userType:'ALL', uid:'', partnerUid:'', datesByMode:{PERIOD:range('PERIOD'),DAILY:null}, page:1,size:20,
      result:EMPTY(), loading:false, exporting:false, error:'', requestId:0, settingsOpen:false,
      columnOrder:fields.slice(), selectedColumns:DEFAULT_COLUMNS.slice(),
      applied:null,
      pickerOptions:{ disabledDate(d) { const end=beijingYesterday();return d.getFullYear()+'-'+String(d.getMonth()+1).padStart(2,'0')+'-'+String(d.getDate()).padStart(2,'0') > end } }
    }
  },
  computed: {
    dates: {
      get () { return this.datesByMode[this.mode] },
      set (dates) { this.datesByMode[this.mode]=dates }
    },
    movableColumns: {
      get () { return this.columnOrder.filter(key=>key!=='uid') },
      set (keys) { this.columnOrder=['uid',...keys] }
    },
    visibleColumns () { return this.columnOrder.filter(k=>this.selectedColumns.includes(k)).map(key=>({
      key, label:key==='date'&&this.mode==='PERIOD'?'统计区间':this.label(key), width:key==='date'?210:key==='uid'?125:Math.max(150,Math.min(210,this.label(key).length*12))
    })) },
    summaryColumns () { return this.visibleColumns.filter(c=>!['uid','activityReward','contractRate','spotRate'].includes(c.key)) },
    canExport () { return hasPerm(this.$store.state.userInfo || {}, 'tenant:partner_data_statistic:export') }
  },
  mounted () {
    try {
      const saved=JSON.parse(localStorage.getItem('partner-statistics-columns-v1')||'null')
      if(saved && Array.isArray(saved.order) && Array.isArray(saved.visible)) {
        this.columnOrder=[...new Set(['uid'].concat(saved.order.filter(k=>fields.includes(k)),fields))]
        this.selectedColumns=[...new Set(['uid'].concat(saved.visible.filter(k=>fields.includes(k))))]
      }
    } catch(e) { /* Unavailable storage must not prevent reporting. */ }
    this.search()
  },
  beforeDestroy () { this.requestId++ },
  methods: {
    t (key) { return translate(key) },
    label (key) { return columnLabels[fields.indexOf(key)] },
    saveColumns () { try {localStorage.setItem('partner-statistics-columns-v1',JSON.stringify({order:this.columnOrder,visible:this.selectedColumns}))} catch(e) {} },
    selectAll () { this.selectedColumns=fields.slice();this.saveColumns() },
    restoreColumns () { this.columnOrder=fields.slice();this.selectedColumns=DEFAULT_COLUMNS.slice();this.saveColumns() },
    display (row,key) {
      const value=row.values[key]
      if(value===null||value===undefined||value==='') return '—'
      if(key==='uid' && value==='RETAIL') return this.t('RETAIL')
      if(key==='contractRate'||key==='spotRate') return new Decimal(value).mul(100).toDecimalPlaces(4,Decimal.ROUND_DOWN).toFixed()+'%'
      return value
    },
    amountClass (row,key) { const v=row.values[key];return typeof v==='string' && /^-\d/.test(v)?'negative':'' },
    // Both enabled user-type options use the existing ordinary-user endpoint this phase.
    params () { return {mode:this.mode,uid:this.uid||undefined,partnerUid:this.partnerUid||undefined,startDate:this.dates&&this.dates[0],endDate:this.dates&&this.dates[1]} },
    beforeModeChange () { return !this.exporting && this.valid() },
    changeMode () { if(!this.dates)this.dates=range(this.mode);this.result=EMPTY();this.search() },
    valid () {
      if([this.uid,this.partnerUid].some(x=>x && (!/^[1-9]\d{0,18}$/.test(x)||new Decimal(x).gt('9223372036854775807')))) {this.$message.warning(this.t('invalidUid'));return false}
      if(!this.dates||this.dates.length!==2||this.dates[0]>this.dates[1]||this.dates[1]>beijingYesterday()
        ||(Date.parse(this.dates[1])-Date.parse(this.dates[0]))/86400000>91) {this.$message.warning(this.t('INVALID_RANGE'));return false}
      return true
    },
    search () { if(!this.valid())return;this.page=1;this.applied=this.params();this.fetch() },
    async fetch () {
      // Keep the total while loading: setting it to zero resets Element's internal page to 1.
      const id=++this.requestId;this.loading=true;this.error=''
      try {
        const response=await http.get({url:'/ta/admin/channel/partnerStatistics/list',data:{...this.applied,page:this.page,size:this.size}})
        if(id!==this.requestId)return
        if(response.code!==0||!response.data)throw new Error(response.msg||'failed')
        this.result=response.data
      } catch(e) {
        if(id===this.requestId) {
          this.result={...EMPTY(),total:this.result.total}
          this.error=this.t(typeof e==='string'?e:e.message||'failed')
        }
      }
      finally {if(id===this.requestId)this.loading=false}
    },
    reset () {this.uid='';this.partnerUid='';this.userType='ALL';this.dates=range(this.mode);this.search()},
    changePage (page) {this.page=page;this.fetch()},
    changeSize (size) {this.size=size;this.page=1;this.fetch()},
    async exportReport () {
      if(!this.canExport||this.loading||this.exporting||!this.valid())return
      this.exporting=true
      const query=this.params()
      let url
      try {
        const blob=await http.get({url:'/ta/admin/channel/partnerStatistics/export',data:query,responseType:'blob'})
        if(!(blob instanceof Blob))throw new Error('failed')
        if(blob.type.includes('json')) {
          const error=JSON.parse(await blob.text())
          throw new Error(error.msg||error.message||'failed')
        }
        if(!blob.size||!blob.type.includes('spreadsheetml'))throw new Error('failed')
        url=URL.createObjectURL(blob)
        const link=document.createElement('a')
        link.href=url
        const stamp=new Date(Date.now()+8*3600000).toISOString().replace(/[-:T]/g,'').slice(0,14)
        link.download='用户合伙人数据统计表'+(query.mode==='PERIOD'?'时间段汇总':'每日明细')+stamp+'.xlsx'
        document.body.appendChild(link)
        link.click()
        link.remove()
        this.$message.success(this.t('exportDone'))
      } catch(e) {this.$message.error(this.t(typeof e==='string'?e:e.message||'failed'))}
      finally {if(url)URL.revokeObjectURL(url);this.exporting=false}
    }
  }
}
</script>

<style scoped>
.partner-statistics { color: #25324b; display:block; height:auto; min-width:0; }
.partner-statistics /deep/ .el-table { flex:none; }
.partner-statistics /deep/ .el-table__body-wrapper::-webkit-scrollbar { height:10px; }
.report-breadcrumb { margin-bottom:20px; }
.report-heading,.table-actions,.mode-row,.detail-title { display:flex; align-items:center; justify-content:space-between; gap:16px; }
.report-heading { margin-bottom:18px; }
h2 { margin:0 0 8px; font-size:20px; font-weight:600; }
.muted { color:#8993a5; font-size:12px; }
.filters { margin-bottom:20px; border-radius:6px; }
.mode-row { justify-content:flex-start; margin-bottom:18px; }
.user-type { width:135px; }
.filters /deep/ .el-form-item { margin-bottom:10px; }
.filters .uid-input { width:185px; }
.filters /deep/ .el-date-editor { width:270px; }
.table-actions { margin:14px 0; }
.summary-table /deep/ th { background:#f1f5fc; }
.summary-table /deep/ td { background:#f8faff; }
.detail-title { margin:12px 0; justify-content:flex-start; }
.negative { color:#d95561; }
.uncertain { color:#ad7400; }
.pagination { text-align:right; margin-top:20px; }
.column-list { max-height:calc(90vh - 160px); overflow-y:auto; }
.column-grid { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); column-gap:16px; }
.column-setting { display:flex; align-items:center; min-width:0; padding:8px 4px; border-bottom:1px solid #f0f2f6; }
.column-setting /deep/ .el-checkbox { display:flex; align-items:center; min-width:0; margin-right:0; }
.column-setting /deep/ .el-checkbox__label { white-space:normal; overflow-wrap:anywhere; line-height:20px; }
.column-setting /deep/ .el-checkbox__input { flex-shrink:0; }
.column-setting > i { flex-shrink:0; }
.fixed-column .el-icon-lock { margin-right:14px; color:#a1a9b6; }
.drag-handle { margin-right:14px; cursor:move; color:#a1a9b6; }
@media (max-width:1100px) { .column-grid { grid-template-columns:repeat(3,minmax(0,1fr)); } }
@media (max-width:800px) { .column-grid { grid-template-columns:repeat(2,minmax(0,1fr)); } }
@media (max-width:520px) { .column-grid { grid-template-columns:minmax(0,1fr); } }
</style>

<style>
.partner-column-dialog { max-width:1280px; }
.partner-column-dialog .el-dialog__body { padding:16px 24px 24px; }
</style>
